/**
 * Light-touch company branding (ADR-0007, mockup v7). ITA and BL use the real
 * logos from MSA Creator; CC and GDM colors are placeholders until their brand
 * files arrive. Orange text/buttons use #B4561C for contrast (DocVault guide).
 */
import itaLogo from "../assets/brand/logo-ita.png";
import blLogo from "../assets/brand/logo-bl.png";
export {
  TYPE_COLORS, TYPE_LABELS, TYPE_NAMES, AREA_LABELS, AREA_CODES, TYPE_CODES, FRAMEWORKS, VIDEO_TYPE_CODES, VIDEO_EXTENSIONS,
  CLASSIFICATION_CODES, CLASSIFICATION_LABELS, RETENTION_CODES, RETENTION_LABELS, VISIBILITIES, VISIBILITY_LABELS, RESTRICTED,
  BUMPS, BUMP_LABELS, nextVersion, fmtVersion, STATUS_CODES,
  MAX_FILE_BYTES, MAX_VIDEO_BYTES, MAX_FRAMEWORK_TAGS, isVideoFile, normalizeFramework, normalizeFrameworks,
} from "../../../shared/codes";
export type { Bump } from "../../../shared/codes";

export interface TenantBrand { name: string; accent: string; ink: string; tint: string; logo: string | null; placeholder?: boolean }

const BUILTIN_LOGO: Record<string, string> = { ITA: itaLogo, BL: blLogo };

export const BRAND: Record<string, TenantBrand> = {
  ITA: { name: "ITAssurance", accent: "#DC6D27", ink: "#B4561C", tint: "#FBEEE5", logo: itaLogo },
  BL: { name: "Balancelogic", accent: "#DC6D27", ink: "#B4561C", tint: "#FBEEE5", logo: blLogo },
  CC: { name: "ConsultraCloud", accent: "#2F6FB3", ink: "#245A93", tint: "#E6EEF8", logo: null, placeholder: true },
  GDM: { name: "Generate Digital Marketing", accent: "#1F8A5B", ink: "#17694A", tint: "#E3F2EA", logo: null, placeholder: true },
};
/** Display order everywhere companies are listed. */
export const CO_ORDER = ["ITA", "BL", "CC", "GDM"];
export const byCoOrder = <T extends { code: string }>(a: T, b: T) =>
  (CO_ORDER.indexOf(a.code) + 1 || 99) - (CO_ORDER.indexOf(b.code) + 1 || 99);

export const GROUP = { name: "Company-wide", accent: "#3E4651" };

export const coName = (code: string, fallback?: string) => (code === "CPY" ? GROUP.name : BRAND[code]?.name ?? fallback ?? code);
export const coColor = (code: string) => (code === "CPY" ? GROUP.accent : BRAND[code]?.accent ?? "#3E4651");

// ---------------------------------------------------------------- admin branding (ADR-0015)
const hexToRgb = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];
const rgbToHex = (c: number[]) => "#" + c.map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, "0")).join("").toUpperCase();
const lum = (h: string) => {
  const [r, g, b] = hexToRgb(h).map((v) => { const x = v / 255; return x <= 0.03928 ? x / 12.92 : ((x + 0.055) / 1.055) ** 2.4; });
  return 0.2126 * r! + 0.7152 * g! + 0.0722 * b!;
};
export const contrast = (a: string, b: string) => { const [x, y] = [lum(a), lum(b)].sort((m, n) => n - m); return (x! + 0.05) / (y! + 0.05); };
/** Darkest-needed shade of the accent for text and buttons: 4.5:1 on the page background. */
export function inkFor(accent: string): string {
  let c = hexToRgb(accent).map(Number);
  for (let i = 0; i < 40 && contrast(rgbToHex(c), "#F6F7F9") < 4.5; i++) c = c.map((v) => v * 0.94);
  return rgbToHex(c);
}
export const tintFor = (accent: string) => rgbToHex(hexToRgb(accent).map((v) => 255 - (255 - v) * 0.12));

/** Company names, accents and logos as the admins set them (fall back to the built-in ones). */
export function applyCompanyBrands(list: { code: string; name: string; accent: string; logo_url?: string | null }[]) {
  for (const c of list) {
    const b = (BRAND[c.code] ??= { name: c.name, accent: c.accent, ink: inkFor(c.accent), tint: tintFor(c.accent), logo: null });
    b.name = c.name;
    if (/^#[0-9a-f]{6}$/i.test(c.accent) && c.accent.toUpperCase() !== b.accent.toUpperCase()) {
      b.accent = c.accent.toUpperCase(); b.ink = inkFor(b.accent); b.tint = tintFor(b.accent); b.placeholder = false;
    }
    if (c.logo_url) { b.logo = c.logo_url; b.placeholder = false; }
    else if (c.logo_url === null) b.logo = BUILTIN_LOGO[c.code] ?? null;
  }
}

/** DocVault's own colors: used for "All my companies" and for a company whose brand isn't set up yet. */
export const DEFAULT_BRAND = { accent: "#DC6D27", ink: "#B4561C", tint: "#FBEEE5" };

/** Set the accent CSS variables for the active workspace. */
export function applyAccent(code: string) {
  const b = BRAND[code];
  const t = !b || b.placeholder ? DEFAULT_BRAND : b;
  const r = document.documentElement.style;
  r.setProperty("--accent", t.accent);
  r.setProperty("--accent-ink", t.ink);
  r.setProperty("--accent-tint", t.tint);
}

/**
 * The main colors of a logo, most prominent first (up to 5). Ignores transparent,
 * near-white and near-grey pixels, so the result is the brand's actual colors.
 * Works on an uploaded File or a URL the browser may read (same origin or CORS).
 */
export async function logoColors(src: Blob | string): Promise<string[]> {
  const blob = typeof src === "string" ? await (await fetch(src)).blob() : src;
  const bmp = await createImageBitmap(blob);
  const scale = Math.min(1, 200 / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale)), h = Math.max(1, Math.round(bmp.height * scale));
  const cv = document.createElement("canvas"); cv.width = w; cv.height = h;
  const ctx = cv.getContext("2d"); if (!ctx) return [];
  ctx.drawImage(bmp, 0, 0, w, h);
  const px = ctx.getImageData(0, 0, w, h).data;
  const buckets = new Map<number, { n: number; r: number; g: number; b: number }>();
  for (let i = 0; i < px.length; i += 4) {
    const [r, g, b, a] = [px[i]!, px[i + 1]!, px[i + 2]!, px[i + 3]!];
    if (a < 200) continue;
    const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
    if (mn > 225) continue;                       // white / paper
    if (mx - mn < 28 && mx > 60) continue;        // greys (keep near-black navy-ish darks out too)
    const k = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
    const e = buckets.get(k) ?? { n: 0, r: 0, g: 0, b: 0 };
    e.n++; e.r += r; e.g += g; e.b += b; buckets.set(k, e);
  }
  const ranked = [...buckets.values()].sort((x, y) => y.n - x.n).map((e) => [e.r / e.n, e.g / e.n, e.b / e.n] as [number, number, number]);
  const out: [number, number, number][] = [];
  for (const c of ranked) {
    if (out.every((o) => Math.hypot(o[0] - c[0], o[1] - c[1], o[2] - c[2]) > 60)) out.push(c);
    if (out.length === 5) break;
  }
  return out.map(rgbToHex);
}

export const ACR_CATS: Record<string, string> = {
  Security: "#D9486B", Identity: "#7C5CC4", Network: "#3B82C4", Cloud: "#1F9E8F", Operations: "#DC6D27", Compliance: "#2F9E5B", Business: "#3E4651",
};

/** Chart colors (data colors, not decoration). */
export const CH = { pub: "#2F9E5B", rev: "#E0A31A", chg: "#D9486B", draft: "#8A94A3", over: "#AE2A20" };
